import { useCallback, useEffect, useRef, useState } from 'react';
import Player from 'video.js/dist/types/player';

import * as Styled from './menu.styles';

type Props = {
  player: Player;
  button: () => React.ReactNode;
  children: (hideMenu: () => void) => React.ReactNode[];
};
export const Menu: React.FC<Props> = ({ player, button, children }) => {
  const menuRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const hideMenu = useCallback(() => {
    setIsVisible(false);
    buttonRef.current?.focus();
  }, [setIsVisible, buttonRef]);

  useEffect(() => {
    player.toggleClass('xing-vjs-menu-visible', isVisible);
  }, [player, isVisible]);

  useEffect(() => {
    const handleEvent = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.preventDefault();
        hideMenu();
      }
    };

    if (isVisible) {
      window.addEventListener('keydown', handleEvent);
    }

    return () => {
      if (isVisible) {
        window.removeEventListener('keydown', handleEvent);
      }
    };
  }, [hideMenu, isVisible]);

  useEffect(() => {
    const handleEvent = (ev: MouseEvent): void => {
      if (!isVisible) return;

      const node = ev.target as Node;
      if (menuRef.current?.contains(node)) {
        return;
      }
      if (buttonRef.current?.contains(node)) {
        return;
      }

      ev.preventDefault();
      ev.stopPropagation();
      hideMenu();
    };

    window.addEventListener('mousedown', handleEvent, true);
    return () => {
      window.removeEventListener('mousedown', handleEvent, true);
    };
  }, [isVisible, hideMenu]);

  return (
    <>
      <Styled.Button
        ref={buttonRef}
        className="vjs-control vjs-button"
        onClick={() => setIsVisible(!isVisible)}
        onBlur={(ev) => {
          if (isVisible && !menuRef.current?.contains(ev.relatedTarget)) {
            hideMenu();
          }
        }}
      >
        {button()}
      </Styled.Button>
      <Styled.Container $isVisible={isVisible} tabIndex={-1}>
        <ul
          ref={menuRef}
          role="menu"
          tabIndex={-1}
          onBlur={(ev) => {
            if (!menuRef.current?.contains(ev.relatedTarget)) {
              hideMenu();
            }
          }}
        >
          {children(hideMenu).map((child, index) => (
            <Styled.MenuItem key={index}>{child}</Styled.MenuItem>
          ))}
        </ul>
      </Styled.Container>
    </>
  );
};
